import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { Reveal, WhatsAppButton } from '@/components/atoms';
import { Section } from '@/components/organisms';
import { introVehicles } from '@/config/introVehicles';

import { routes as copy, ROUTES_TITLE_ID, WHATSAPP_MESSAGE } from '../content';
import { useDeliveryRoutes } from '../hooks/useDeliveryRoutes';
import { useDeliveryRouteTabs } from '../hooks/useDeliveryRouteTabs';
import { RoutePricePanel, RouteTabs } from '../molecules';

/**
 * Where Kabex delivers, and what it costs to get there.
 *
 * This band used to be three sentences in three equal tiles, which answered
 * "where" and left "how much" to a calculator on another page — so the page
 * about delivery was the one page that could not price it. The destinations
 * are a switch now, and the panel beside it carries the figure and the five
 * weight bands behind it, read from the same settings the calculator uses.
 *
 * The band decides nothing: the routes come from `useDeliveryRoutes` and which
 * one is showing from `useDeliveryRouteTabs`.
 */
export function DeliveryRoutes() {
  const { routes, effectiveFrom } = useDeliveryRoutes();
  const { selected, selectedIndex, select, tabId, panelId } = useDeliveryRouteTabs(routes);

  return (
    <Section
      pattern="rings"
      labelledBy={ROUTES_TITLE_ID}
      /* The Land Cruiser the intro opens on, carried into the band that prices
         its journey, so the figure is quoted over the same silhouette the
         visitor arrived at. */
      backdrop={{ picture: introVehicles['land-cruiser'].image }}
    >
      <Grid container spacing={{ xs: 8, md: 12 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          {/* The sticky box is outside the `Reveal`: a sticky element can only
              travel inside its own containing block, and `Reveal` is a block
              that wraps its content, so sticking within it would give the
              column no room to move. Out here the containing block is the grid
              item, which stretches to the tallest column in the row. */}
          <Box sx={{ position: { md: 'sticky' }, top: { md: 120 } }}>
            <Reveal direction="left">
              <Stack spacing={5} alignItems="flex-start">
                <Typography variant="overline" sx={{ color: 'primary.main' }}>
                  {copy.eyebrow}
                </Typography>
                <Typography id={ROUTES_TITLE_ID} variant="h2" component="h2">
                  {copy.title}
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  {copy.lede}
                </Typography>
                <WhatsAppButton message={WHATSAPP_MESSAGE} emphasis="outline" />
              </Stack>
            </Reveal>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          <Stack spacing={{ xs: 5, md: 6 }}>
            <RouteTabs
              routes={routes}
              selectedIndex={selectedIndex}
              onSelect={select}
              tabId={tabId}
              panelId={panelId}
              label={copy.tabsLabel}
            />
            <RoutePricePanel
              route={selected}
              effectiveFrom={effectiveFrom}
              id={panelId}
              labelledBy={tabId(selectedIndex)}
            />
          </Stack>
        </Grid>
      </Grid>
    </Section>
  );
}
